import React from "react";
import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import ExplorationPageShell from "./ExplorationPageShell";

/** The card grid of the module list pages beside the panel: one column on
 *  a narrow screen, two from the lg breakpoint up. */
const PANEL_CARD_GRID_SX = {
  display: "grid",
  gap: 2,
  width: "100%",
  gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
};

/**
 * The page frame of the list pages (lasers, substrates, calibrations,
 * explorations, selections, reproductions), laid out like the New
 * exploration page: the header with the title, the cards below it (a
 * column, or a two-column grid with `grid`) and the sticky panel beside
 * them with the filters and actions; the panel carries the count. The
 * column shows the error, the loading line or the empty state in place of
 * the cards.
 * `error` is a message (shown in an alert) or a ready-made alert element.
 */
export default function ResourcePageLayout({
  title,
  loading,
  loadingLabel,
  error,
  count,
  emptyText,
  panel,
  grid = false,
  children,
}) {
  return (
    <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
      <ExplorationPageShell title={title} noPaper>
        <Box
          sx={{
            display: "grid",
            gap: 3,
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 300px" },
            alignItems: "start",
            width: "100%",
          }}
        >
          <Stack spacing={1.5} sx={{ minWidth: 0 }}>
            {error ? (
              typeof error === "string" ? (
                <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
                  {error}
                </Alert>
              ) : (
                error
              )
            ) : null}
            {loading ? (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3, px: 0.5 }}>
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">
                  {loadingLabel}
                </Typography>
              </Stack>
            ) : count === 0 && !(typeof error === "string") ? (
              <Box
                data-testid="resource-empty"
                sx={{
                  borderRadius: 3,
                  border: "1px dashed rgba(15, 23, 42, 0.2)",
                  bgcolor: "#fcfcfd",
                  px: 3,
                  py: 4,
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {emptyText}
                </Typography>
              </Box>
            ) : grid ? (
              <Box sx={PANEL_CARD_GRID_SX}>{children}</Box>
            ) : (
              children
            )}
          </Stack>
          {panel}
        </Box>
      </ExplorationPageShell>
    </Stack>
  );
}
